import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { Autocomplete, Stack, TextField } from '@mui/material';
import {
  GainsightCategoryInput,
  IntegrationChannelType,
  IntegrationDescriptionInput,
} from 'generated/graphql';
import { useState } from 'react';
import useGetIntegrationEditFormData from 'v2/core/repository/edge-server/integrations/editIntegrations/useGetIntegrationEditFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const AddGainsightIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  integrationData,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const [selectedCategory, setSelectedCategory] =
    useState<GainsightCategoryInput | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Gainsight,
      integrationType,
    });

  const { data: integrationFormData, status: integrationFormStatus } =
    useGetIntegrationEditFormData({ integrationID });

  const categories = integrationFormData?.gainsight?.categoryList ?? [];

  const existingCategories =
    integrationData.gainsight?.subscribedCategories ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Gainsight,
    integrationType,
    hasValidFormFields: !!selectedCategory,
    descriptionInput,
    currentDescription: '',
  });

  const integrateGainsightCategory = async (
    selectedCategory: GainsightCategoryInput | null,
    addAnother = false,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedCategory) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedCategory.id,
          },
        ),
      };
    }

    // Remove duplicates
    const selectedCategories = [
      ...new Map(
        [...existingCategories, selectedCategory].map((category) => [
          category.id,
          category,
        ]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        gainsight: {
          subscribedCategories: selectedCategories,
          shouldIngestIdeas: false,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedCategory(null);
    setDescriptionInput('');
  };

  const addedCategoryIDsSet = new Set(existingCategories.map((s) => s.id));

  const options = categories.filter(
    (category) => !addedCategoryIDsSet.has(category.id),
  );

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a Gainsight Category" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.Gainsight}
            integrationType={integrationType}
          />
          <Autocomplete
            fullWidth
            multiple={false}
            value={selectedCategory}
            options={options}
            getOptionLabel={(option) => option?.name}
            onChange={(_, selected) =>
              selected && setSelectedCategory(selected)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Gainsight Category"
                required
              />
            )}
            componentsProps={{ paper: { elevation: 8 } }}
            loading={IsLoading(integrationFormStatus)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            clearIcon={null}
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isValidationInProgress={isValidatingDescription}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() =>
          integrateGainsightCategory(selectedCategory, true)
        }
        onIntegrate={() => integrateGainsightCategory(selectedCategory)}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedCategory || !!descriptionInput
        }
      />
    </Stack>
  );
};
